import { useTenderDeadline } from "../../../../../hooks/useTenderDeadline";
import { useTenderActionError } from "../../../../../hooks/useTenderActionError";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import cn from "classnames";
import { useTranslation } from "react-i18next";
import style from "./ComplexOrderBidBlock.module.scss";
import RebidNotice from "../../../order-info/components/RebidNotice/RebidNotice";
import CustomButton from "../../../../custom/custom-button/CustomButton";
import ConfirmActionModal from "../ConfirmActionModal/ConfirmActionModal";
import UpdateComplexBidModal from "./UpdateComplexBidModal";
import HintIcon from "../../../../shared/icons/HintIcon";
import CanceledIcon from "../../../../shared/icons/CanceledIcon";
import { CURRENCIES } from "../../../../../mock";
import { Expeditor } from "../../../../../services";
import { TenderDeadlineText } from "../../../../shared/TenderDeadlineText/TenderDeadlineText";
import { isRailwayOrder } from "../../../../../pages/orders/utils/railwayOrder";

const formatPrice = (value, currency = "KZT") => {
  if (value === null || value === undefined) return "-";
  const formatted = Number(value).toLocaleString("ru-RU").replace(/,/g, " ");
  const resolvedCurrency = currency || "KZT";
  const symbol =
    resolvedCurrency === "KZT" ? "₸" : CURRENCIES[resolvedCurrency]?.symbol || resolvedCurrency;
  return `${formatted} ${symbol}`;
};

const formatPosition = (bid, t) => {
  if (!bid?.position || !bid?.totalBids) return "-";
  return t("complexTender.expeditorBid.positionOf", {
    position: bid.position,
    total: bid.totalBids,
  });
};

const getBestPrice = (bid) => {
  if (bid?.minPrice !== null && bid?.minPrice !== undefined) return bid.minPrice;
  if (bid?.bestPrice !== null && bid?.bestPrice !== undefined) return bid.bestPrice;
  return bid?.price;
};

const ComplexOrderBidBlock = ({
  onRefreshOrder,
  bid,
  order,
  isCanceled = false,
  canceledText = "",
  onUpdateData,
  onFindNewOrders,
}) => {
  const { isExpired: isTenderExpiredByServer } = useTenderDeadline(order?.tenderDateEnd);
  const { t } = useTranslation();
  const { requestErrorDisplay } = useTenderActionError(order, { onRefreshOrder });
  const { user } = useSelector((state) => state);
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [revokeModalOpen, setRevokeModalOpen] = useState(false);
  const [isRevoking, setIsRevoking] = useState(false);

  if (!bid) return null;

  const isLeading = bid.position === 1;
  const isTenderEnded = order?.tenderDateEnd && isTenderExpiredByServer;
  const isOtherExpeditorAssigned = !!order?.expeditorId && order.expeditorId !== user?.companyId;
  const bestPrice = getBestPrice(bid);

  const handleRevoke = async () => {
    if (!bid?.id) return;

    setIsRevoking(true);
    try {
      await Expeditor.deleteComplexOrderBid(bid.id);
      toast.success(t("complexTender.expeditorBid.bidRevoked"));
      setRevokeModalOpen(false);
      if (onUpdateData) onUpdateData();
    } catch (error) {
      await requestErrorDisplay(error, t, "complexTender.expeditorBid.revokeBidError");
    } finally {
      setIsRevoking(false);
    }
  };

  return (
    <div className={style.card}>
      <div className={style.header}>
        <span className={style.title}>
          {t(order?.assignmentMethod === "Auction" ? "complexTender.auctionTitle" : "complexTender.title")}
        </span>
        {order?.tenderDateEnd && (
          <span className={style.deadline}><TenderDeadlineText date={order.tenderDateEnd} /></span>
        )}
      </div>

      {order?.isInRebid === true && !isTenderEnded && (
        <RebidNotice>
          {t(
            order?.assignmentMethod === "Auction"
              ? "orderInfoExpeditor.bid.auctionRebidNotice"
              : "orderInfoExpeditor.bid.rebidNotice",
          )}
        </RebidNotice>
      )}

      <div className={cn(style.stats_grid, { [style.stats_grid_2col]: !order?.acceptedVehiclesCount })}>
        <div className={style.your_bid}>
          <span className={style.label}>{t("complexTender.expeditorBid.yourBid")}</span>
          <span className={style.your_bid_price}>{formatPrice(bid.price, bid.currency)}</span>
        </div>

        <div className={style.stat_cell}>
          <span className={style.label}>
            {t("complexTender.expeditorBid.offeredVehicles", {
              context: isRailwayOrder(order) ? "railway" : undefined,
            })}
          </span>
          <span className={style.stat_value}>
            {bid.vehiclesCount ? t("complexTender.units.pieces", { count: bid.vehiclesCount }) : "-"}
          </span>
        </div>

        {order?.acceptedVehiclesCount && (
          <div className={style.stat_cell}>
            <span className={style.label}>{t("complexTender.expeditorBid.allocatedToYou")}</span>
            <span className={style.stat_value}>
              {bid?.acceptedVehiclesCount ? t("complexTender.units.pieces", { count: bid.acceptedVehiclesCount }) : "-"}
            </span>
          </div>
        )}

        {order?.assignmentMethod === "Auction" && !bid.isMultiCurrency && (
          <>
            <div className={style.stat_cell}>
              <span className={style.label}>{t("complexTender.expeditorBid.bestBid")}</span>
              <span className={style.stat_value}>{formatPrice(bestPrice, bid.currency)}</span>
            </div>
            <div className={style.stat_cell}>
              <span className={style.label}>{t("complexTender.expeditorBid.yourPlace")}</span>
              <span className={isLeading ? style.position_lead : style.position_behind}>
                {formatPosition(bid, t)}
              </span>
            </div>
          </>
        )}
      </div>

      {isCanceled ? (
        <div className={style.hint_box_danger}>
          <CanceledIcon />
          <span className={style.hint_text_danger}>{canceledText}</span>
        </div>
      ) : isOtherExpeditorAssigned ? (
        <div className={style.hint_box_danger}>
          <HintIcon color="#E5373C" />
          <span className={style.hint_text_danger}>{t("complexTender.expeditorBid.tenderEnded")}</span>
        </div>
      ) : isTenderEnded && (
        <div className={style.hint_box}>
          <HintIcon />
          <span className={style.hint_text}>
            {t(
              order?.assignmentMethod === "Auction"
                ? "complexTender.expeditorBid.awaitAuctionDecision"
                : "complexTender.expeditorBid.awaitDecision",
            )}
          </span>
        </div>
      )}
       {/* : !isLeading && !bid.isMultiCurrency && (
        <div className={style.hint_box}>
          <HintIcon />
          <span className={style.hint_text}>{t("complexTender.expeditorBid.lowerBidHint")}</span>
        </div>
      )} */}

      {isTenderEnded || isOtherExpeditorAssigned || isCanceled ? (
        <div className={style.buttons_row}>
          <CustomButton
            text={t("complexTender.expeditorBid.findNewOrders")}
            isNavy
            onClick={onFindNewOrders}
            style={{ width: "100%" }}
          />
        </div>
      ) : (
        <div className={style.buttons_row}>
          <div className={style.btn_primary}>
            <CustomButton
              text={t("complexTender.expeditorBid.changeBid")}
              isNavy
              onClick={() => setUpdateModalOpen(true)}
              style={{ width: "100%" }}
            />
          </div>
          <div className={style.btn_secondary}>
            <CustomButton
              text={t("complexTender.expeditorBid.revokeBid")}
              isNavy
              isWhite
              onClick={() => setRevokeModalOpen(true)}
              style={{ width: "100%" }}
            />
          </div>
        </div>
      )}

      <UpdateComplexBidModal
        onRefreshOrder={onRefreshOrder}
        isOpen={updateModalOpen && !isTenderEnded}
        onClose={() => setUpdateModalOpen(false)}
        bid={bid}
        order={order}
        onSuccess={() => {
          setUpdateModalOpen(false);
          if (onUpdateData) onUpdateData();
        }}
      />

      <ConfirmActionModal
        isOpen={revokeModalOpen && !isTenderEnded}
        title={t("complexTender.expeditorBid.revokeBid")}
        message={t("complexTender.expeditorBid.revokeBidConfirm")}
        confirmText={t("common.yes")}
        cancelText={t("common.no")}
        onConfirm={handleRevoke}
        onCancel={() => setRevokeModalOpen(false)}
        isLoading={isRevoking}
      />
    </div>
  );
};

export default ComplexOrderBidBlock;
